import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState
       } from '@angular/material/dialog';

import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { Subscription } from 'rxjs';
import { ProgressService } from './progress.service';

@Injectable()
export class BackNavigationService implements OnDestroy
{
  private static readonly GUARD_DEPTH_KEY: string =
      'productDesignerBackNavigationDepth';

  private guardDepth: number = 0;
  private isRetractingGuards: boolean = false;

  private readonly subscriptions: Subscription = new Subscription();
  private readonly popStateListener: (event: PopStateEvent) => void =
      this.respondToPopState.bind(this);
  private readonly userActivationListener: () => void =
      this.alignGuardsWithBackTargets.bind(this);

  constructor(
      @Inject(ToolboxService) private toolboxService: ToolboxService,
      @Inject(ProgressService) private progressService: ProgressService,
      @Inject(MatDialog) private dialog: MatDialog)
  { }

  public activate(): void
  {
    if (this.guardDepthOf(window.history.state) > 0) {
      window.history.replaceState(undefined, '');
    }

    window.addEventListener('popstate', this.popStateListener);
    window.addEventListener('pointerup', this.userActivationListener, true);
    window.addEventListener('keydown', this.userActivationListener, true);

    this.subscriptions.add(this.toolboxService.targetChanged.subscribe(
        () => this.alignGuardsWithBackTargets()));
    this.subscriptions.add(this.progressService.progressChanged.subscribe(
        () => this.alignGuardsWithBackTargets()));
    this.subscriptions.add(this.dialog.afterOpened.subscribe(dialogRef => {
      this.alignGuardsWithBackTargets();
      dialogRef.afterClosed().subscribe(
          () => this.alignGuardsWithBackTargets());
    }));
  }

  public ngOnDestroy(): void
  {
    window.removeEventListener('popstate', this.popStateListener);
    window.removeEventListener('pointerup', this.userActivationListener, true);
    window.removeEventListener('keydown', this.userActivationListener, true);
    this.subscriptions.unsubscribe();
  }

  private openDialogs(): Array<MatDialogRef<unknown>>
  {
    return this.dialog.openDialogs.filter(
        dialogRef => dialogRef.getState() === MatDialogState.OPEN);
  }

  private countBackTargets(): number
  {
    return this.openDialogs().length
        + this.toolboxService.getActiveToolboxCount()
        + this.progressService.getPreviousProgressCount();
  }

  private navigateBack(): boolean
  {
    const dialogs = this.openDialogs();
    if (dialogs.length > 0) {
      const topmostDialog = dialogs[dialogs.length - 1];
      if (topmostDialog.disableClose) {
        return false;
      }
      topmostDialog.close();
      return true;
    }

    if (this.toolboxService.getActiveToolboxCount() > 0) {
      this.toolboxService.closeLastOpenedToolbox();
      return true;
    }

    if (this.progressService.getPreviousProgressCount() > 0) {
      this.progressService.previousProgressRequested.emit();
      return true;
    }

    return false;
  }

  private alignGuardsWithBackTargets(): void
  {
    if (this.isRetractingGuards) {
      return;
    }

    const backTargetCount = this.countBackTargets();
    while (this.guardDepth < backTargetCount) {
      this.guardDepth++;
      window.history.pushState(
          {[BackNavigationService.GUARD_DEPTH_KEY]: this.guardDepth}, '');
    }

    if (this.guardDepth > backTargetCount) {
      this.isRetractingGuards = true;
      window.history.go(backTargetCount - this.guardDepth);
    }
  }

  private respondToPopState(event: PopStateEvent): void
  {
    const previousGuardDepth = this.guardDepth;
    this.guardDepth = this.guardDepthOf(event.state);

    if (this.isRetractingGuards) {
      this.isRetractingGuards = false;
    }
    else if (this.guardDepth < previousGuardDepth && this.navigateBack()) {
      return;
    }

    this.alignGuardsWithBackTargets();
  }

  private guardDepthOf(state: any): number
  {
    return state?.[BackNavigationService.GUARD_DEPTH_KEY] ?? 0;
  }
}
